import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import { X, AlertTriangle, Quote } from 'lucide-react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Icon, Type, LinkIcon, Video, Image, BookOpen, FileText, DynamicIsland, PdfDropZone } from '../../../components/ui';
import Input, { InputLabel, InputGroup } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Input';
import { cn } from '../../../lib/cn';
import QuestionClusterBuilder from '../../../components/teacher/QuestionClusterBuilder';
import QuestionCreationForm from '../../../components/questions/QuestionCreationForm';
import ImportModal from '../../../components/questions/ImportModal';
import PdfDisplay from '../../../components/stimulus/PdfDisplay';
import CompactLessonFlow from '../../../components/teacher/CompactLessonFlow';

const QUESTION_GENERATION_STEPS = [
  'Analyzing passage...',
  'Identifying key concepts...',
  'Calling Claude...',
  'Generating multiple choice questions...',
  'Crafting short answer prompts...',
  'Developing essay questions...',
  'Creating rubrics...',
  'Formulating explanations...',
  'Validating questions...',
  'Saving to database...',
];

const QUESTION_IMPORT_STEPS = [
  'Extracting text...',
  'Analyzing questions...',
  'Identifying question types...',
  'Parsing answer choices...',
  'Detecting Bloom\'s levels...',
  'Structuring results...',
];

export default function TeacherActivitiesEdit({ activity, stimulus, questions, errors, available_chapters, lesson_context, students = [], submission_count, active_question_generation }) {
  const { url } = usePage();
  const fromLesson = useMemo(() => {
    try {
      const params = new URLSearchParams(url.split('?')[1] || '');
      return params.get('from_lesson');
    } catch { return null; }
  }, [url]);

  const scrollTitle = lesson_context
    ? (<><Link href={`/teacher/courses/${lesson_context.course_id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{lesson_context.course_title}</Link>{'  ›  '}<Link href={fromLesson || lesson_context.from_lesson || `/teacher/courses/${lesson_context.course_id}/units/${lesson_context.unit_id}/lessons/${lesson_context.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{lesson_context.title}</Link>{'  ›  '}{activity.title}</>)
    : activity.title;

  const [activeTab, setActiveTab] = useState(stimulus ? 'questions' : 'stimulus');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [activityDetails, setActivityDetails] = useState({
    title: activity.title,
    description: activity.description || '',
  });

  const handleSaveDetails = () => {
    router.patch(`/teacher/activities/${activity.id}`, { activity: activityDetails }, {
      onSuccess: () => setIsEditingDetails(false),
    });
  };

  const handleCreateSession = () => {
    router.post(`/teacher/activities/${activity.id}/sessions`);
  };

  const handleReturnToSession = () => {
    router.visit(`/teacher/sessions/${activity.active_session.id}`);
  };

  const tabs = [
    { id: 'stimulus', label: 'Stimulus', badge: stimulus ? (stimulus.chapters?.length > 1 ? `${stimulus.chapters.length}` : '1') : null },
    { id: 'questions', label: 'Questions', badge: questions?.length > 0 ? `${activity.approved_count}/${questions.length}` : null },
  ];

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      {/* Compact Lesson Flow */}
      {lesson_context && (
        <CompactLessonFlow lessonContext={lesson_context} />
      )}

      <TeacherPageHeader
        title={activity.title}
        subtitle={activity.description || 'No description'}
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Reseed from props: the server may have re-titled the activity
                // after a stimulus change since this page first mounted.
                setActivityDetails({ title: activity.title, description: activity.description || '' });
                setIsEditingDetails(true);
              }}
            >
              Edit details
            </Button>
            <Link href={`/teacher/activities/${activity.id}/submissions`}>
              <Button variant="secondary" size="sm">
                Submissions ({submission_count})
              </Button>
            </Link>
            {students.length > 0 && (
              <PrintWorksheetMenu activityId={activity.id} students={students} />
            )}
            {activity.active_session ? (
              <Button
                variant="primary"
                onClick={handleReturnToSession}
              >
                Return to Session
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!activity.ready_for_session}
                onClick={handleCreateSession}
              >
                Create Session
              </Button>
            )}
          </div>
        }
      />

      {/* Edit Details Modal */}
      {isEditingDetails && (
        <EditDetailsModal
          activityId={activity.id}
          activityDetails={activityDetails}
          setActivityDetails={setActivityDetails}
          onSave={handleSaveDetails}
          onClose={() => setIsEditingDetails(false)}
          fromLesson={fromLesson}
          defaultBackPath={activity.default_back_path}
          hasStimulus={activity.has_stimulus}
          approvedCount={activity.approved_count}
        />
      )}

      {/* Errors */}
      {errors && errors.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
          <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Status Banner */}
      {!activity.ready_for_session && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
          <p className="text-sm text-yellow-800 dark:text-yellow-400">
            <strong>Not ready for session:</strong> You need{' '}
            {!stimulus && 'a stimulus'}
            {!stimulus && activity.approved_count === 0 && ' and '}
            {activity.approved_count === 0 && 'at least one approved question'}
            {' '}before creating a session.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'pb-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900 dark:border-white dark:text-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              )}
            >
              {tab.label}
              {tab.badge && (
                <span className={cn(
                  'ml-2 px-2 py-0.5 text-xs rounded-full',
                  activeTab === tab.id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                )}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'stimulus' && (
        <StimulusTab activityId={activity.id} stimulus={stimulus} availableChapters={available_chapters} />
      )}

      {activeTab === 'questions' && (
        <QuestionsTab activityId={activity.id} stimulus={stimulus} questions={questions || []} activeGeneration={active_question_generation} />
      )}
    </TeacherShell>
  );
}

function EditDetailsModal({ activityId, activityDetails, setActivityDetails, onSave, onClose, fromLesson, defaultBackPath, hasStimulus, approvedCount }) {
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const canSuggest = hasStimulus && approvedCount > 0;

  const handleSuggest = async () => {
    setIsSuggesting(true);
    setSuggestError(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/activities/${activityId}/suggest_metadata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.activity_title && !data.activity_description) {
          setSuggestError("Couldn't generate suggestions — try again.");
        } else {
          setActivityDetails(prev => ({
            ...prev,
            ...(data.activity_title ? { title: data.activity_title } : {}),
            ...(data.activity_description ? { description: data.activity_description } : {}),
          }));
        }
      } else {
        setSuggestError("Couldn't generate suggestions — try again.");
      }
    } catch {
      setSuggestError("Couldn't generate suggestions — try again.");
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this activity? This will also delete all associated questions and sessions. This action cannot be undone.')) {
      const redirectTo = fromLesson || defaultBackPath || '/teacher/dashboard';
      router.delete(`/teacher/activities/${activityId}`, {
        onSuccess: () => router.visit(redirectTo),
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-lg p-6 m-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Edit Activity Details</h3>

        <div className="space-y-4">
          <InputGroup>
            <InputLabel htmlFor="edit-title" required>Title</InputLabel>
            <Input
              id="edit-title"
              value={activityDetails.title}
              onChange={(e) => setActivityDetails(prev => ({ ...prev, title: e.target.value }))}
            />
          </InputGroup>

          <InputGroup>
            <InputLabel htmlFor="edit-description">Objectives</InputLabel>
            <Textarea
              id="edit-description"
              value={activityDetails.description}
              onChange={(e) => setActivityDetails(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </InputGroup>
        </div>

        <div className="flex items-center justify-between mt-6">
          {canSuggest ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={isSuggesting} onClick={handleSuggest}>
                {isSuggesting ? 'Suggesting...' : 'Suggest from stimulus'}
              </Button>
              {suggestError && (
                <span className="text-xs text-red-600 dark:text-red-400">{suggestError}</span>
              )}
            </div>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={onSave}>Save</Button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Permanently delete this activity and all its content.
          </p>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            Delete Activity
          </Button>
        </div>
      </Card>
    </div>
  );
}

function StimulusTab({ activityId, stimulus, availableChapters }) {
  const hasChapters = availableChapters && availableChapters.length > 0;
  const [stimulusType, setStimulusType] = useState(stimulus?.stimulus_type || 'article');
  const [content, setContent] = useState(stimulus?.content || '');
  const [url, setUrl] = useState(stimulus?.url || '');
  const [title, setTitle] = useState(stimulus?.title || '');
  const [selectedChapterIds, setSelectedChapterIds] = useState([]);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(!stimulus);

  // Reset form when stimulus changes (e.g., after delete)
  useEffect(() => {
    setStimulusType(stimulus?.stimulus_type || 'article');
    setContent(stimulus?.content || '');
    setUrl(stimulus?.url || '');
    setTitle(stimulus?.title || '');
    setSelectedChapterIds([]);
    setFile(null);
    setIsEditing(!stimulus);
  }, [stimulus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const method = stimulus ? 'patch' : 'post';
    const path = `/teacher/activities/${activityId}/stimulus`;
    const onFinish = () => { setIsSubmitting(false); setIsEditing(false); };

    if (stimulusType === 'chapter' && selectedChapterIds.length > 0) {
      router[method](path, { chapter_ids: selectedChapterIds }, { onFinish });
    } else if (stimulusType === 'pdf') {
      const fd = new FormData();
      fd.append('stimulus[stimulus_type]', 'pdf');
      if (title) fd.append('stimulus[title]', title);
      if (file) fd.append('stimulus[file]', file);
      router[method](path, fd, { forceFormData: true, onFinish });
    } else {
      router[method](path, {
        stimulus: { stimulus_type: stimulusType, content, url, title }
      }, { onFinish });
    }
  };

  const stimulusTypes = [
    ...(hasChapters ? [{ id: 'chapter', label: 'Book Chapter', icon: BookOpen }] : []),
    { id: 'article', label: 'Article / Text', icon: Type },
    { id: 'pdf', label: 'PDF', icon: FileText },
    { id: 'url', label: 'URL / Link', icon: LinkIcon },
    { id: 'video', label: 'Video', icon: Video },
    { id: 'image', label: 'Image', icon: Image },
  ];

  return (
    <div className="max-w-3xl space-y-6">
      {/* Stimulus Preview */}
      {stimulus && !isEditing && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {stimulus.title && (
                <h3 className="font-semibold text-gray-900 dark:text-white">{stimulus.title}</h3>
              )}
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 capitalize">
                {stimulus.stimulus_type}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          </div>

          {/* Book link for chapter-type stimuli */}
          {stimulus.stimulus_type === 'chapter' && stimulus.book_link && (
            <a
              href={`/teacher/library/${stimulus.book_link.book_id}?chapter=${stimulus.book_link.chapter_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-3"
            >
              <Icon icon={BookOpen} size="sm" />
              <span>{stimulus.book_link.book_title}</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}

          <StimulusPreview stimulus={stimulus} />
        </div>
      )}

      {/* Edit Form */}
      {isEditing && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <InputGroup>
            <InputLabel>Stimulus Type</InputLabel>
            <div className={cn('grid gap-3', hasChapters ? 'grid-cols-2 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4')}>
              {stimulusTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    setStimulusType(type.id);
                    setSelectedChapterIds([]);
                  }}
                  className={cn(
                    'p-4 rounded-lg border-2 text-center transition-all',
                    stimulusType === type.id
                      ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                  )}
                >
                  <Icon icon={type.icon} size="lg" className="mx-auto mb-1 text-gray-700 dark:text-gray-300" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{type.label}</span>
                </button>
              ))}
            </div>
          </InputGroup>

          {/* Chapter Selection */}
          {stimulusType === 'chapter' && hasChapters && (
            <div className="space-y-2">
              {availableChapters.length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const allIds = availableChapters.map(ch => ch.id);
                    setSelectedChapterIds(prev =>
                      prev.length === allIds.length ? [] : allIds
                    );
                  }}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                >
                  {selectedChapterIds.length === availableChapters.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
              {availableChapters.map((ch) => (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => {
                    setSelectedChapterIds(prev =>
                      prev.includes(ch.id) ? prev.filter(id => id !== ch.id) : [...prev, ch.id]
                    );
                  }}
                  className={cn(
                    'w-full text-left p-4 rounded-lg border-2 transition-all',
                    selectedChapterIds.includes(ch.id)
                      ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
                  )}
                >
                  <p className="font-medium text-gray-900 dark:text-white">
                    Chapter {ch.position + 1}: {ch.title}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>{ch.book_title}</span>
                    {ch.word_count > 0 && <span>{ch.word_count.toLocaleString()} words</span>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {stimulusType !== 'chapter' && (
            <InputGroup>
              <InputLabel htmlFor="stimulus-title">Title (optional)</InputLabel>
              <Input
                id="stimulus-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., The Declaration of Independence"
              />
            </InputGroup>
          )}

          {(stimulusType === 'url' || stimulusType === 'video') && (
            <InputGroup>
              <InputLabel htmlFor="stimulus-url" required>URL</InputLabel>
              <Input
                id="stimulus-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
              />
            </InputGroup>
          )}

          {(stimulusType === 'article' || stimulusType === 'image') && (
            <InputGroup>
              <InputLabel htmlFor="stimulus-content" required>
                {stimulusType === 'article' ? 'Content' : 'Image URL'}
              </InputLabel>
              {stimulusType === 'article' ? (
                <Textarea
                  id="stimulus-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste your article text here..."
                  rows={12}
                />
              ) : (
                <Input
                  id="stimulus-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Image URL..."
                />
              )}
            </InputGroup>
          )}

          {stimulusType === 'pdf' && (
            <InputGroup>
              <InputLabel required={!stimulus?.has_file}>PDF file</InputLabel>
              <PdfDropZone
                file={file}
                onFileChange={setFile}
                existingFilename={stimulus?.has_file ? stimulus.file_url?.split('/').pop() : null}
              />
            </InputGroup>
          )}

          <div className="flex items-center gap-3">
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || (stimulusType === 'chapter' && selectedChapterIds.length === 0)}
            >
              {isSubmitting ? 'Saving...' : (stimulus ? 'Update Stimulus' : 'Save Stimulus')}
            </Button>

            {stimulus && (
              <>
                <Button type="button" variant="ghost" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => {
                    if (confirm('Delete this stimulus? This will also remove associated question drafts.')) {
                      router.delete(`/teacher/activities/${activityId}/stimulus`);
                    }
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function StimulusPreview({ stimulus }) {
  if (stimulus.stimulus_type === 'chapter') {
    const chapters = stimulus.chapters || [];
    if (chapters.length === 0) {
      return (
        <Card className="p-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">Chapter content attached</p>
        </Card>
      );
    }
    return (
      <div className="space-y-2">
        {chapters.map((ch) => (
          <a
            key={ch.id}
            href={`/teacher/library/${ch.book_id}?chapter=${ch.id}`}
            className="block w-full text-left p-4 rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 transition-all"
          >
            <p className="font-medium text-gray-900 dark:text-white">
              Chapter {ch.position + 1}: {ch.title}
            </p>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{ch.book_title}</span>
              {ch.word_count > 0 && <span>{ch.word_count.toLocaleString()} words</span>}
            </div>
          </a>
        ))}
      </div>
    );
  }

  if (stimulus.stimulus_type === 'article') {
    return (
      <Card className="p-6 max-h-96 overflow-y-auto">
        <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap text-gray-700 dark:text-gray-300">
          {stimulus.content}
        </div>
      </Card>
    );
  }

  if (stimulus.stimulus_type === 'url') {
    return (
      <Card className="p-6">
        <a
          href={stimulus.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline break-all"
        >
          {stimulus.url}
        </a>
      </Card>
    );
  }

  if (stimulus.stimulus_type === 'video') {
    return (
      <Card className="p-6">
        <a
          href={stimulus.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline break-all"
        >
          {stimulus.url}
        </a>
      </Card>
    );
  }

  if (stimulus.stimulus_type === 'image') {
    return (
      <Card className="p-6">
        {stimulus.content && (
          <img
            src={stimulus.content}
            alt={stimulus.title || 'Stimulus image'}
            className="max-w-full rounded-lg"
          />
        )}
        {stimulus.file_url && (
          <img
            src={stimulus.file_url}
            alt={stimulus.title || 'Stimulus image'}
            className="max-w-full rounded-lg"
          />
        )}
      </Card>
    );
  }

  if (stimulus.has_file && stimulus.file_url) {
    return (
      <PdfDisplay
        title={stimulus.title || 'Attached File'}
        fileUrl={stimulus.file_url}
        pageCount={stimulus.page_count}
      />
    );
  }

  return null;
}

function QuestionsTab({ activityId, stimulus, questions, activeGeneration }) {
  const [clusters, setClusters] = useState([
    { id: 'cluster-1', type: 'multiple_choice', count: 3, bloomLevel: 'understand', prompt: '' },
    { id: 'cluster-2', type: 'short_answer', count: 1, bloomLevel: 'apply', prompt: '' },
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importReviewPath, setImportReviewPath] = useState(null);
  const [addMode, setAddMode] = useState(null); // 'generate' | 'import' | 'write'
  const [showImportModal, setShowImportModal] = useState(false);

  const [generationMessage, setGenerationMessage] = useState(null); // { text, failed }
  const generationPollRef = useRef(null);

  const stopGenerationPoll = () => {
    if (generationPollRef.current) {
      clearInterval(generationPollRef.current);
      generationPollRef.current = null;
    }
  };

  const finishGeneration = (data) => {
    stopGenerationPoll();
    setIsGenerating(false);
    setGenerationMessage({ text: data.message, failed: data.failed });
    router.reload({ only: ['questions'], preserveScroll: true });
  };

  // Generation runs in a background job; poll its status until it finishes.
  const pollGeneration = (generationId) => {
    stopGenerationPoll();
    setIsGenerating(true);
    generationPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `/teacher/activities/${activityId}/stimulus/question_generations/${generationId}`,
          { headers: { 'Accept': 'application/json' } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'completed' || data.status === 'failed') finishGeneration(data);
      } catch { /* continue polling */ }
    }, 3000);
  };

  // Resume polling if the page was (re)loaded while a run was in flight.
  useEffect(() => {
    if (activeGeneration?.id) pollGeneration(activeGeneration.id);
    return stopGenerationPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGeneration?.id]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationMessage(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/activities/${activityId}/stimulus/generate_questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          clusters: clusters.map((c, index) => ({
            type: c.type,
            count: c.count,
            bloom_level: c.bloomLevel,
            prompt: c.prompt || null,
            position: index,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setIsGenerating(false);
        setGenerationMessage({ text: data.error || "Couldn't start generation — try again.", failed: true });
        return;
      }
      pollGeneration(data.id);
    } catch {
      setIsGenerating(false);
      setGenerationMessage({ text: "Couldn't start generation — try again.", failed: true });
    }
  };

  const handleApprove = (questionId) => {
    router.post(`/teacher/questions/${questionId}/approve`);
  };

  const handleApproveAll = () => {
    const pending = questions.filter(q => !q.teacher_approved);
    if (pending.length === 0) return;

    pending.forEach((question, index) => {
      setTimeout(() => {
        router.post(`/teacher/questions/${question.id}/approve`, {}, {
          preserveScroll: true,
        });
      }, index * 100);
    });
  };

  const handleReject = (questionId) => {
    if (confirm('Remove this question?')) {
      router.delete(`/teacher/questions/${questionId}/reject`);
    }
  };

  const handleManualCreated = () => {
    setAddMode(null);
    router.reload();
  };

  const handleImportStarted = ({ import_id, review_path }) => {
    setIsImporting(true);
    setImportReviewPath(review_path);

    // Poll for completion
    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `/teacher/activities/${activityId}/question_imports/${import_id}`,
          { headers: { 'Accept': 'application/json' } }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'ready_for_review' || data.status === 'completed') {
            clearInterval(poll);
            setIsImporting(false);
            router.visit(review_path);
          } else if (data.status === 'failed') {
            clearInterval(poll);
            setIsImporting(false);
            router.visit(review_path);
          }
        }
      } catch { /* continue polling */ }
    }, 2000);

    // Clean up on unmount
    return () => clearInterval(poll);
  };

  const pendingQuestions = questions.filter(q => !q.teacher_approved);
  const approvedQuestions = questions.filter(q => q.teacher_approved);
  const hasQuestions = questions.length > 0;

  const addOptions = [
    { id: 'generate', label: 'AI Generate', desc: 'Generate from stimulus', disabled: !stimulus },
    { id: 'import', label: 'Import', desc: 'Upload .docx, .pdf, or paste' },
    { id: 'write', label: 'Write', desc: 'Create manually' },
  ];

  return (
    <div className="space-y-8">
      {/* Dynamic Island for generation/import progress */}
      <DynamicIsland
        isActive={isGenerating || isImporting}
        steps={isImporting ? QUESTION_IMPORT_STEPS : QUESTION_GENERATION_STEPS}
        stepDuration={1500}
      />

      {generationMessage?.text && (
        <div
          className={cn(
            'flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm',
            generationMessage.failed
              ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200'
              : 'border-green-200 bg-green-50 text-green-800 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-200'
          )}
          role="status"
        >
          <span>{generationMessage.text}</span>
          <button
            type="button"
            onClick={() => setGenerationMessage(null)}
            className="shrink-0 opacity-70 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          activityId={activityId}
          onClose={() => setShowImportModal(false)}
          onImportStarted={handleImportStarted}
        />
      )}

      {/* Pending Questions */}
      {pendingQuestions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Pending Review ({pendingQuestions.length})
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleApproveAll}
            >
              Approve All
            </Button>
          </div>
          <div className="space-y-4">
            {pendingQuestions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
          </div>
        </div>
      )}

      {/* Approved Questions */}
      {approvedQuestions.length > 0 && (
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Approved ({approvedQuestions.length})
          </h3>
          <div className="space-y-4">
            {approvedQuestions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                approved
              />
            ))}
          </div>
        </div>
      )}

      {/* Add Questions Section */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
          {hasQuestions ? 'Add More Questions' : 'Add Questions'}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {hasQuestions
            ? 'Add more questions to your activity.'
            : 'Choose how to add questions to this activity.'}
        </p>

        {/* Mode selector */}
        {!addMode && (
          <div className="grid grid-cols-3 gap-3">
            {addOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={opt.disabled}
                onClick={() => {
                  if (opt.id === 'import') {
                    setShowImportModal(true);
                  } else {
                    setAddMode(opt.id);
                  }
                }}
                className={cn(
                  'p-4 rounded-lg border-2 text-center transition-all',
                  opt.disabled
                    ? 'border-gray-100 dark:border-gray-800 opacity-40 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-400 cursor-pointer'
                )}
              >
                <span className="block text-sm font-medium text-gray-900 dark:text-white">{opt.label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.desc}</span>
              </button>
            ))}
          </div>
        )}

        {/* AI Generate mode */}
        {addMode === 'generate' && (
          <div className="space-y-6">
            <QuestionClusterBuilder
              clusters={clusters}
              onChange={setClusters}
              disabled={isGenerating}
            />
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <Button
                variant="primary"
                onClick={handleGenerate}
                disabled={isGenerating || clusters.length === 0}
              >
                {isGenerating ? 'Generating...' : (hasQuestions ? 'Generate Questions' : 'Generate Draft Questions')}
              </Button>
              <Button variant="ghost" onClick={() => setAddMode(null)}>
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Manual write mode */}
        {addMode === 'write' && (
          <div>
            <QuestionCreationForm
              activityId={activityId}
              onCreated={handleManualCreated}
              onCancel={() => setAddMode(null)}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

const qualityFlagLabels = {
  guessable: 'Guessable without the text',
  answer_longest: 'Answer is the longest option',
  anchor_not_found: 'Quote not found in the text',
};

function QuestionCard({ question, onApprove, onReject, approved }) {
  const [isEditing, setIsEditing] = useState(false);

  const typeLabels = {
    multiple_choice: 'Multiple Choice',
    mcq: 'Multiple Choice',
    short_answer: 'Short Answer',
    essay: 'Essay',
    table_completion: 'Categorization',
  };

  const bloomLabels = {
    remember: 'Remember',
    understand: 'Understand',
    apply: 'Apply',
    analyze: 'Analyze',
    evaluate: 'Evaluate',
    create: 'Create',
  };

  const isMultipleChoice = question.question_type === 'multiple_choice' || question.question_type === 'mcq';

  // Normalize options: imported questions store [{text, correct}], AI-generated store {a: "text", b: "text"}
  const normalizedOptions = (() => {
    if (!isMultipleChoice || !question.options) return null;
    if (Array.isArray(question.options)) {
      return question.options.map((opt, i) => ({
        key: String.fromCharCode(97 + i),
        text: opt.text,
        isCorrect: opt.correct === true,
      }));
    }
    return Object.entries(question.options).sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => ({
      key,
      text: value,
      isCorrect: question.correct_answer?.toLowerCase() === key.toLowerCase(),
    }));
  })();
  const hasOptions = normalizedOptions && normalizedOptions.length > 0;

  if (isEditing) {
    return (
      <QuestionEditForm
        question={question}
        approved={approved}
        onCancel={() => setIsEditing(false)}
        onSaved={() => {
          setIsEditing(false);
          router.reload({ preserveScroll: true });
        }}
      />
    );
  }

  return (
    <Card id={`q-${question.id}`} className={cn(
      'p-5',
      approved && 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
    )}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
            {typeLabels[question.question_type] || question.question_type}
          </span>
          {question.bloom_level && (
            <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">
              {bloomLabels[question.bloom_level] || question.bloom_level}
            </span>
          )}
          {question.ai_drafted && !approved && (
            <span className="text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded">
              AI Draft
            </span>
          )}
          {approved && (
            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded">
              Approved
            </span>
          )}
          {!approved && (question.quality_flags || []).map((flag) => (
            <span
              key={flag}
              className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded"
            >
              <AlertTriangle className="w-3 h-3" />
              {qualityFlagLabels[flag] || flag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditing(true)}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
            title="Edit question"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <span className="text-xs text-gray-400 dark:text-gray-500">#{question.position + 1}</span>
        </div>
      </div>

      {/* Question Prompt */}
      <p className="text-gray-900 dark:text-white whitespace-pre-wrap font-medium">{question.content}</p>

      {/* Multiple Choice Options */}
      {hasOptions && (
        <div className="mt-4 space-y-2">
          {normalizedOptions.map((opt) => (
            <div
              key={opt.key}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border',
                opt.isCorrect
                  ? 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
                  : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
              )}
            >
              <span className={cn(
                'flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold',
                opt.isCorrect
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
              )}>
                {opt.key.toUpperCase()}
              </span>
              <span className={cn(
                'text-sm',
                opt.isCorrect
                  ? 'text-green-800 dark:text-green-300 font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              )}>
                {opt.text}
                {opt.isCorrect && <span className="ml-2 text-green-600 dark:text-green-400">{'\u2713'} Correct</span>}
                {!opt.isCorrect && question.distractor_rationales?.[opt.key]?.why && (
                  <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {question.distractor_rationales[opt.key].why}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Passage the question is anchored to */}
      {question.anchor?.quote && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/10 dark:border-amber-800">
          <p className="flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1 uppercase">
            <Quote className="w-3 h-3" />
            From the text
            {question.anchor.paragraph_start && (
              <span className="normal-case font-normal">
                {' '}· paragraph {question.anchor.paragraph_start}
                {question.anchor.paragraph_end && question.anchor.paragraph_end !== question.anchor.paragraph_start && `–${question.anchor.paragraph_end}`}
              </span>
            )}
          </p>
          <p className="text-sm text-gray-800 dark:text-gray-200 font-serif whitespace-pre-wrap">{question.anchor.quote}</p>
        </div>
      )}

      {/* Table Completion (Categorization) Options */}
      {question.question_type === 'table_completion' && question.options?.bank && (
        <div className="mt-4 space-y-4">
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase">Item Bank</p>
            <div className="flex flex-wrap gap-2">
              {question.options.bank.map((item, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
          {question.options.columns && (
            <div className={cn(
              'grid gap-3',
              question.options.columns.length === 2 && 'grid-cols-2',
              question.options.columns.length === 3 && 'grid-cols-3',
              question.options.columns.length >= 4 && 'grid-cols-2 md:grid-cols-4'
            )}>
              {question.options.columns.map((column, index) => (
                <div
                  key={index}
                  className="p-3 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-700"
                >
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2 uppercase">
                    {column.label}
                  </p>
                  <div className="space-y-1">
                    {(column.correctItems || []).map((item, itemIndex) => (
                      <span
                        key={itemIndex}
                        className="block px-2 py-1 bg-green-100 dark:bg-green-800/30 rounded text-sm text-green-800 dark:text-green-300"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Model Answer for Short Answer/Essay */}
      {!isMultipleChoice && question.model_answer && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-700">
          <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1 uppercase">Model Answer</p>
          <p className="text-sm text-blue-900 dark:text-blue-200 whitespace-pre-wrap">{question.model_answer}</p>
        </div>
      )}

      {/* Rubric for Essay */}
      {question.question_type === 'essay' && question.rubric && (
        <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg dark:bg-purple-900/20 dark:border-purple-700">
          <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-1 uppercase">Rubric</p>
          <p className="text-sm text-purple-900 dark:text-purple-200 whitespace-pre-wrap">{question.rubric}</p>
        </div>
      )}

      {/* Explanation */}
      {question.explanation && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase">Explanation</p>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{question.explanation}</p>
        </div>
      )}

      {!approved && (
        <div className="mt-4 flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={() => onApprove(question.id)}>
            Approve
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onReject(question.id)}>
            Remove
          </Button>
        </div>
      )}
    </Card>
  );
}

function QuestionEditForm({ question, approved, onCancel, onSaved }) {
  const [prompt, setPrompt] = useState(question.content || '');
  const [explanation, setExplanation] = useState(question.explanation || '');
  const [modelAnswer, setModelAnswer] = useState(question.model_answer || '');
  const [rubric, setRubric] = useState(question.rubric || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isMultipleChoice = question.question_type === 'multiple_choice' || question.question_type === 'mcq';

  // For MCQ, manage options as editable array
  const initialOptions = (() => {
    if (!isMultipleChoice || !question.options) return [];
    if (Array.isArray(question.options)) {
      return question.options.map((opt, i) => ({
        text: opt.text,
        correct: opt.correct === true,
      }));
    }
    // Object format
    const entries = Object.entries(question.options).sort(([a], [b]) => a.localeCompare(b));
    return entries.map(([key, value]) => ({
      text: value,
      correct: question.correct_answer?.toLowerCase() === key.toLowerCase(),
    }));
  })();
  const [options, setOptions] = useState(initialOptions);

  const handleOptionChange = (index, field, value) => {
    setOptions(prev => prev.map((opt, i) => {
      if (i !== index) {
        // If setting correct, unset others
        if (field === 'correct' && value === true) return { ...opt, correct: false };
        return opt;
      }
      return { ...opt, [field]: value };
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setError(null);

    const body = {
      question: {
        prompt,
        explanation: explanation || null,
        model_answer: modelAnswer || null,
        rubric: rubric || null,
      }
    };

    // For MCQ, send options in array format
    if (isMultipleChoice && options.length > 0) {
      body.question.options = options.map(opt => ({ text: opt.text, correct: opt.correct }));
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/teacher/questions/${question.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Unexpected response from server');
      }

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.errors?.join(', ') || 'Save failed');
      }

      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <Card className={cn(
      'p-5',
      approved && 'border-blue-300 dark:border-blue-700'
    )}>
      <div className="space-y-4">
        {/* Prompt */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">
            Question Prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            className={inputClass}
          />
        </div>

        {/* MCQ Options */}
        {isMultipleChoice && options.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 block">
              Options
            </label>
            <div className="space-y-2">
              {options.map((opt, idx) => {
                const letter = String.fromCharCode(65 + idx);
                return (
                  <div key={idx} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => handleOptionChange(idx, 'correct', true)}
                      className={cn(
                        'flex items-center justify-center w-7 h-9 rounded-full text-xs font-semibold cursor-pointer transition-colors shrink-0',
                        opt.correct
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300 hover:bg-green-200 dark:hover:bg-green-800'
                      )}
                      title={opt.correct ? 'Correct answer' : 'Click to mark correct'}
                    >
                      {letter}
                    </button>
                    <input
                      type="text"
                      value={opt.text}
                      onChange={(e) => handleOptionChange(idx, 'text', e.target.value)}
                      className={cn(inputClass, 'flex-1')}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Model Answer (short answer / essay) */}
        {!isMultipleChoice && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">
              Model Answer
            </label>
            <textarea
              value={modelAnswer}
              onChange={(e) => setModelAnswer(e.target.value)}
              rows={3}
              className={inputClass}
            />
          </div>
        )}

        {/* Rubric (essay only) */}
        {question.question_type === 'essay' && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">
              Rubric
            </label>
            <textarea
              value={rubric}
              onChange={(e) => setRubric(e.target.value)}
              rows={2}
              className={inputClass}
            />
          </div>
        )}

        {/* Explanation */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">
            Explanation
          </label>
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={2}
            className={inputClass}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-1">
          <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {error && (
            <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Print Worksheet Menu ───────────────────────────────────
// Small dropdown that lets the teacher pick an enrolled student and open the
// scannable QR-stamped worksheet PDF (/teacher/activities/:id/scan_pdf) in a
// new tab. When there's exactly one student, skips the menu and opens the PDF
// directly.

function PrintWorksheetMenu({ activityId, students }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const scanPdfUrl = (studentId) =>
    `/teacher/activities/${activityId}/scan_pdf?student_id=${studentId}`;

  if (students.length === 1) {
    return (
      <a
        href={scanPdfUrl(students[0].id)}
        target="_blank"
        rel="noopener"
        className="inline-flex"
      >
        <Button variant="secondary" size="sm">
          Print Worksheet
        </Button>
      </a>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen((v) => !v)}
      >
        Print Worksheet
      </Button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Print for student
            </p>
          </div>
          {students.map((student) => (
            <a
              key={student.id}
              href={scanPdfUrl(student.id)}
              target="_blank"
              rel="noopener"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300 flex items-center justify-center text-xs font-medium shrink-0">
                {student.initials}
              </div>
              <span className="text-gray-900 dark:text-white truncate">
                {student.full_name}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
